'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RootLoading, TermsAcceptanceDialog, TermsAcceptanceErrorDialog } from '@workspace/ui';
import {
  hasB2CTermsRole,
  hasValidTermsStatus,
  TERMS_ACCEPTANCE_COPY,
  TERMS_AND_CONDITIONS_DOCUMENT_URL,
} from '@workspace/ui/lib';
import { ApiError } from '@workspace/ui/types';
import { useState } from 'react';
import { toast } from 'sonner';
import { logout } from '@/actions/mixed/auth';
import { acceptTerms } from '@/actions/terms';
import { useUserVerification } from '@/hooks/use-user';
import { useAuthStore } from '@/stores/authStore';
import { useNpsStore } from '@/stores/npsStore';

export function TermsAcceptanceGate({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const roles = useAuthStore((s) => s.user?.roles);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const queryClient = useQueryClient();

  const isEnabled = isAuthenticated && Boolean(accessToken) && hasB2CTermsRole(roles);
  const { data, isPending, isError, isFetching, refetch } = useUserVerification(isEnabled);

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await acceptTerms();
      if (!response.success) {
        throw new Error(response.error || response.message || TERMS_ACCEPTANCE_COPY.acceptError);
      }
      return response;
    },
    onSuccess: async () => {
      setHasAccepted(true);
      await queryClient.invalidateQueries({ queryKey: ['user-verification'] });
    },
    onError: (error) => {
      const message =
        error instanceof ApiError || error instanceof Error
          ? error.message
          : TERMS_ACCEPTANCE_COPY.acceptError;
      toast.error(message);
    },
  });

  async function handleLogout() {
    setIsLoggingOut(true);

    try {
      await logout();
      useNpsStore.getState().reset();
      localStorage.removeItem('onboarding_check_completed');
      window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
    } catch {
      setIsLoggingOut(false);
      toast.error(TERMS_ACCEPTANCE_COPY.logoutError);
    }
  }

  const requiresAction = !hasAccepted && data?.terms?.requires_action === true;
  const cannotConfirmTerms = isError || !hasValidTermsStatus(data?.terms);

  if (!isLoggingOut) {
    if (!isEnabled) return children;
    if (isPending) return <RootLoading />;
  }

  if (cannotConfirmTerms) {
    return (
      <TermsAcceptanceErrorDialog
        isRetrying={isFetching && !isPending}
        isLoggingOut={isLoggingOut}
        onRetry={() => {
          void refetch();
        }}
        onLogout={() => {
          void handleLogout();
        }}
      />
    );
  }

  if (!isLoggingOut && !requiresAction) return children;

  return (
    <TermsAcceptanceDialog
      open
      documentUrl={data?.terms?.document_url || TERMS_AND_CONDITIONS_DOCUMENT_URL}
      isPending={mutation.isPending}
      isLoggingOut={isLoggingOut}
      onAccept={() => mutation.mutate()}
      onLogout={() => {
        void handleLogout();
      }}
      title={TERMS_ACCEPTANCE_COPY.title}
      description={TERMS_ACCEPTANCE_COPY.description}
      documentLabel={TERMS_ACCEPTANCE_COPY.documentLabel}
      checkboxLabel={TERMS_ACCEPTANCE_COPY.checkboxLabel}
      buttonLabel={TERMS_ACCEPTANCE_COPY.button}
      buttonLoadingLabel={TERMS_ACCEPTANCE_COPY.buttonLoading}
      logoutLabel={TERMS_ACCEPTANCE_COPY.logout}
      logoutLoadingLabel={TERMS_ACCEPTANCE_COPY.logoutLoading}
    />
  );
}
